import { useState } from 'react';
import { Combobox } from '@spark-web/combobox';
import { Field, FieldProps } from '@spark-web/field';
import { useAddressSearch, useDebounce } from '../../../hooks';

type Nullable<T> = T | null;

type Props = {
  /**
   * Label for address search input
   * @default 'Address'
   */
  label?: string;
  /**
   * Input delay to call address api in ms
   * @default 500
   */
  debounce?: number;
  onChange?: (value: string | null) => void;
  defaultOption?: { option: AddressOption; position?: 'start' | 'end' };
} & Pick<FieldProps, 'tone' | 'message' | 'disabled'>;

interface AddressOption {
  value: string;
}

function AddressAutocomplete({ label = 'Address', tone, message, debounce = 500, onChange, defaultOption }: Props) {
  const [value, setValue] = useState<Nullable<AddressOption>>(null);
  const [inputValue, setInputValue] = useState('');
  const debouncedSearchTerm = useDebounce(inputValue, debounce);
  const { data: options, isFetching } = useAddressSearch(debouncedSearchTerm);

  const handleInputChange = (value: string) => setInputValue(value);

  const handleChange = (newValue: Nullable<AddressOption>) => {
    setValue(newValue);
    onChange?.(newValue?.value as string | null);
  };
  const items: AddressOption[] = options?.map((option: string) => ({ value: option })) ?? [];

  return (
    <Field label={label} tone={tone} message={message}>
      <Combobox
        placeholder="Start typing..."
        items={items}
        isLoading={isFetching}
        inputValue={inputValue || ''}
        onInputChange={handleInputChange}
        value={value}
        onChange={handleChange}
        getOptionLabel={(option: AddressOption) => option.value}
        getOptionValue={(option: AddressOption) => option.value}
        defaultOption={inputValue && inputValue.trim().length > 2 ? defaultOption : undefined}
        disableFilter
      />
    </Field>
  );
}

export default AddressAutocomplete;
